import { useState, type CSSProperties, type ReactNode } from "react";

/**
 * Fælles billedbehandling: 1px hårlinje-kant, skarpe hjørner, varm duotone
 * (grayscale + sepia) med svagt sepia-slør; på hover blødt fuld farve + scale.
 * Wrappes med tokeniseret .fl-img (se src/styles.css).
 *
 * `fallback` peger på et motif der vises hvis `src` mangler eller `<img>`
 * fejler i indlæsning. Fallback rendres i "papir-tryk"-look via `.is-fallback`.
 */
export type EditorialImageProps = {
  src: string | null | undefined;
  alt: string;
  ratio: string;
  play?: boolean;
  duration?: string | null;
  credit?: ReactNode;
  className?: string;
  width?: number;
  height?: number;
  sizes?: string;
  srcSet?: string;
  fallback?: string;
};

export function EditorialImage({
  src,
  alt,
  ratio,
  play = false,
  duration,
  credit,
  className = "",
  width,
  height,
  sizes,
  srcSet,
  fallback,
}: EditorialImageProps) {
  const style: CSSProperties = { aspectRatio: ratio };
  const [failed, setFailed] = useState(false);
  const useFallback = (!src || failed) && !!fallback;
  const showImage = (src && !failed) || useFallback;
  const finalSrc = useFallback ? fallback : src;
  return (
    <>
      <div
        className={`fl-img ${useFallback ? "is-fallback" : ""} ${className}`.trim()}
        style={style}
      >
        {showImage ? (
          <img
            src={finalSrc as string}
            alt={alt}
            loading="lazy"
            decoding="async"
            width={width}
            height={height}
            sizes={useFallback ? undefined : sizes}
            srcSet={useFallback ? undefined : srcSet}
            onError={() => {
              if (!failed && fallback) setFailed(true);
            }}
          />
        ) : (
          <div aria-hidden="true" className="w-full h-full" />
        )}
        {play && (
          <span className="fl-play-badge" aria-hidden="true">
            <span>
              <span />
            </span>
          </span>
        )}
        {duration && (
          <span className="fl-duration-badge" aria-hidden="true">
            {duration}
          </span>
        )}
      </div>
      {credit && <div className="photo-credit mt-2">{credit}</div>}
    </>
  );
}

